import { AnchorHashIds } from 'constants/anchor-hash-ids';
import {
    ACCOUNT_MEMBER_ROLES_HELP,
    BRANDING_LINK,
    CAMPAIGN_HELP_CREATE_LINK,
    CREATE_OUTBOUND_EMAIL_LINK,
    IMPORT_CONTACTS_LINK,
    MOBILE_APP_OVERVIEW_LINK,
    NONPROFIT_VERIFICATION_LINK,
    WHAT_IS_GIVEBUTTER_PLUS_LINK,
    WIDGETS_ON_YOUR_SITE_LINK,
    WITHDRAWALS_HOW_TO_LINK,
} from 'constants/links';
import AddressBook from 'images/recommended-actions/address-book.green.svg';
import CircleCheck from 'images/recommended-actions/circle-check.yellow.svg';
import CircleHeart from 'images/recommended-actions/circle-heart.blue.svg';
import Envelope from 'images/recommended-actions/envelope.purple.svg';
import Megaphone from 'images/recommended-actions/megaphone.yellow.svg';
import MobileNotch from 'images/recommended-actions/mobile-notch.purple.svg';
import PaperPlane from 'images/recommended-actions/paper-plane.blue.svg';
import PiggyBank from 'images/recommended-actions/piggy-bank.green.svg';
import Plus from 'images/recommended-actions/plus.yellow.svg';
import Swatchbook from 'images/recommended-actions/swatchbook.purple.svg';
import UserPlus from 'images/recommended-actions/user-plus.green.svg';
import { DurationLike } from 'luxon';

const MAX_TIME_SINCE_ACTION_COMPLETED: DurationLike = { days: 7 };

type RecommendedActionColor = 'yellow' | 'green' | 'purple' | 'blue';

interface IRecommendedAction {
    actionKey: string;
    color: RecommendedActionColor;
    duration: string;
    icon: string;
    title: string;
    description: string;
    getStartedLink: string;
    learnMoreLink: string;
}

const actions: Record<string, IRecommendedAction> = {
    verify_nonprofit: {
        actionKey: 'verifyNonProfit',
        color: 'yellow',
        icon: CircleCheck,
        duration: '5 min',
        title: 'Verify your nonprofit',
        description:
            'Boost donor confidence and unlock benefits like additional payment options and shorter transaction pending periods.',
        getStartedLink: `settings/about/verify#${AnchorHashIds.VerifyNonprofit}`,
        learnMoreLink: NONPROFIT_VERIFICATION_LINK,
    },
    add_logo_and_branding: {
        actionKey: 'addLogoBranding',
        color: 'purple',
        icon: Swatchbook,
        duration: '2 min',
        title: 'Add logo & branding',
        description:
            'Customize your donation pages with your logo and colors to create a trustworthy giving experience.',
        getStartedLink: 'settings/about/branding',
        learnMoreLink: BRANDING_LINK,
    },
    invites_additional_users: {
        actionKey: 'inviteAdditionalUsers',
        color: 'green',
        icon: UserPlus,
        duration: '2 min',
        title: 'Invite additional users',
        description:
            'Invite your team to collaborate on campaigns, share tasks, and raise more together.',
        getStartedLink: 'settings/users',
        learnMoreLink: ACCOUNT_MEMBER_ROLES_HELP,
    },
    setup_widget: {
        actionKey: 'setupWidget',
        color: 'blue',
        icon: CircleHeart,
        duration: '10 min',
        title: 'Add a donation button',
        description:
            'Turn visitors on your website into supporters with just a few clicks. No coding expertise needed!',
        getStartedLink: 'settings/integrations/widgets',
        learnMoreLink: WIDGETS_ON_YOUR_SITE_LINK,
    },
    start_plus_trial: {
        actionKey: 'startPlusTrial',
        color: 'yellow',
        icon: Plus,
        duration: '2 min',
        title: 'Start a free Givebutter Plus trial',
        description:
            'Start a 30-day trial of Givebutter Plus and explore tools that help organizations raise 2.8x more.',
        getStartedLink: '',
        learnMoreLink: WHAT_IS_GIVEBUTTER_PLUS_LINK,
    },
    create_engage_email_campaign: {
        actionKey: 'createFirstMarketingEmail',
        color: 'purple',
        icon: Envelope,
        duration: '12 min',
        title: 'Create your first marketing email',
        description:
            '48% of donors prefer email for updates and appeals. Craft an email campaign using our templates and built-in Canva integration.',
        getStartedLink: 'engage/email',
        learnMoreLink: CREATE_OUTBOUND_EMAIL_LINK,
    },
    import_contacts: {
        actionKey: 'importContacts',
        color: 'green',
        icon: AddressBook,
        duration: '10 min',
        title: 'Import contacts',
        description:
            'Import your contacts to keep all your supporters in one place and strengthen your outreach.',
        getStartedLink: 'contacts',
        learnMoreLink: IMPORT_CONTACTS_LINK,
    },
    send_engage_email_campaign: {
        actionKey: 'sendFirstMarketingEmail',
        color: 'blue',
        icon: PaperPlane,
        duration: '2 min',
        title: 'Send out your first marketing email',
        description:
            'Send your finished email to all of your supporters, or strategically send it to a targeted list. 26% of donors say email most inspires them to give.',
        getStartedLink: 'engage/email',
        learnMoreLink: CREATE_OUTBOUND_EMAIL_LINK,
    },
    create_event_campaign: {
        actionKey: 'createEventCampaign',
        color: 'yellow',
        icon: Megaphone,
        duration: '5 min',
        title: 'Create a campaign for your event',
        description:
            'Set up your event page with ticketing, registration, donations, and even an auction—all in one place.',
        getStartedLink: '',
        learnMoreLink: CAMPAIGN_HELP_CREATE_LINK,
    },
    download_mobile_app: {
        actionKey: 'downloadMobileApp',
        color: 'purple',
        icon: MobileNotch,
        duration: '2 min',
        title: 'Download the mobile app',
        description:
            'Manage events, track donations, and collect payments on the go—right from the Givebutter app.',
        getStartedLink: '',
        learnMoreLink: MOBILE_APP_OVERVIEW_LINK,
    },
    request_payout: {
        actionKey: 'requestPayout',
        color: 'green',
        icon: PiggyBank,
        duration: '2 min',
        title: 'Request payout',
        description: 'Securely transfer funds to your bank and put your donations to work—fast.',
        getStartedLink: 'payouts',
        learnMoreLink: WITHDRAWALS_HOW_TO_LINK,
    },
};

export { actions, MAX_TIME_SINCE_ACTION_COMPLETED };
export type { IRecommendedAction };
